import { useEffect, useState, useCallback } from 'react';
import { streamFlowApi } from '@lib/streamFlowApi';

/**
 * Per-provider seat-usage gauge (claimed / max_connections). Surfaces when a
 * provider is saturated so the operator knows it's time to add another
 * credential (= another provider row = more concurrent seats). Reads the
 * `meta.provider_usage` the active-streams endpoint now returns, so it shares
 * the page's existing 10s poll — no extra fetch.
 */
interface ProviderUsage {
  provider_id: string;
  name: string;
  claimed: number;
  max_connections: number;
  saturated: boolean;
}

export default function ConcurrencyGauge() {
  const [usage, setUsage] = useState<ProviderUsage[]>([]);

  const refresh = useCallback(async () => {
    const res = await streamFlowApi.adminActiveStreams();
    // provider_usage rides on the response envelope's `meta`, which the typed
    // get<T> helper doesn't surface — read it off the raw response.
    const meta = (res as { meta?: { provider_usage?: ProviderUsage[] } }).meta;
    if (meta?.provider_usage) setUsage(meta.provider_usage);
  }, []);

  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), 10_000);
    return () => clearInterval(t);
  }, [refresh]);

  if (usage.length === 0) return null;

  return (
    <div className="rounded-lg border border-gray-200 p-3 mb-4">
      <div className="text-xs font-semibold text-gray-600 mb-2">Provider concurrency (claimed / max seats)</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {usage.map((u) => {
          const frac = u.max_connections > 0 ? u.claimed / u.max_connections : 0;
          const bar = u.saturated ? 'bg-rose-500' : frac >= 0.7 ? 'bg-amber-500' : 'bg-emerald-500';
          return (
            <div key={u.provider_id}>
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium truncate">{u.name}</span>
                <span className="text-gray-500">
                  {u.claimed}/{u.max_connections}
                  {u.saturated && <span className="ml-1 rounded bg-rose-100 px-1 text-[10px] text-rose-700">FULL</span>}
                </span>
              </div>
              <div className="mt-1 h-1.5 w-full rounded bg-gray-100 overflow-hidden">
                <div className={`h-full ${bar}`} style={{ width: `${Math.min(100, Math.round(frac * 100))}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
